/**
 * Collision System - Efficient collision detection for game entities
 * Uses AABB with broad-phase optimization for performance
 */

import {
  checkCollision,
  isWithinDistance,
  type CollisionEntity
} from '../utils/collision';
import type { Player, GameObject, Letter } from '../types';

/**
 * Collision result for player-object interactions
 */
export interface CollisionResult<T = CollisionEntity> {
  entity: T;
  collided: boolean;
}

/**
 * Collision detection configuration
 */
export interface CollisionConfig {
  playerHitboxScale: number;
  objectHitboxScale: number;
  overlapThreshold: number;
  broadPhaseDistance: number;
}

/**
 * Default collision configuration
 * - Player: 80% of visual size (child-friendly)
 * - Objects: 90% of visual size (child-friendly)
 * - Overlap: 40% of smaller entity
 * - Broad-phase: Sum of both sizes for quick rejection
 */
const DEFAULT_CONFIG: CollisionConfig = {
  playerHitboxScale: 0.8,
  objectHitboxScale: 0.9,
  overlapThreshold: 0.4,
  broadPhaseDistance: 200 // Reasonable default for typical entity sizes
};

/**
 * Collision System Class
 */
export class CollisionSystem {
  private config: CollisionConfig;

  constructor(config: Partial<CollisionConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Check collision between player and a single entity
   * @param player - Player entity
   * @param entity - Entity to check against
   * @returns True if collision detected
   */
  checkPlayerCollision(player: Player, entity: CollisionEntity): boolean {
    // Broad-phase: Quick rejection based on distance
    const maxDistance = (player.size + entity.size) / 2 + this.config.broadPhaseDistance;
    if (!isWithinDistance(player, entity, maxDistance)) {
      return false;
    }

    // Narrow-phase: Precise AABB collision detection
    return checkCollision(
      player,
      entity,
      this.config.playerHitboxScale,
      this.config.objectHitboxScale,
      this.config.overlapThreshold
    );
  }

  /**
   * Detect all collisions between player and a list of objects
   * @param player - Player entity
   * @param objects - Array of objects to check
   * @returns Array of collision results
   */
  detectObjectCollisions(
    player: Player,
    objects: GameObject[]
  ): CollisionResult<GameObject>[] {
    const results: CollisionResult<GameObject>[] = [];

    for (const obj of objects) {
      const collided = this.checkPlayerCollision(player, obj);
      if (collided) {
        results.push({ entity: obj, collided: true });
      }
    }

    return results;
  }

  /**
   * Detect all collisions between player and letters
   * @param player - Player entity
   * @param letters - Array of letters to check
   * @returns Array of collision results
   */
  detectLetterCollisions(
    player: Player,
    letters: Letter[]
  ): CollisionResult<Letter>[] {
    const results: CollisionResult<Letter>[] = [];

    for (const letter of letters) {
      // Skip already collected letters
      if (letter.collected) {
        continue;
      }

      const collided = this.checkPlayerCollision(player, letter);
      if (collided) {
        results.push({ entity: letter, collided: true });
      }
    }

    return results;
  }

  /**
   * Detect all collisions in one pass (optimized)
   * Returns the first collision found for immediate response
   * @param player - Player entity
   * @param entities - Array of entities to check
   * @returns First collision found, or null
   */
  detectFirstCollision(
    player: Player,
    entities: CollisionEntity[]
  ): CollisionEntity | null {
    for (const entity of entities) {
      if (this.checkPlayerCollision(player, entity)) {
        return entity;
      }
    }
    return null;
  }

  /**
   * Batch collision detection for multiple entities
   * Returns all colliding entities
   * @param player - Player entity
   * @param entities - Array of entities to check
   * @returns Array of colliding entities
   */
  detectAllCollisions(
    player: Player,
    entities: CollisionEntity[]
  ): CollisionEntity[] {
    const collisions: CollisionEntity[] = [];

    for (const entity of entities) {
      if (this.checkPlayerCollision(player, entity)) {
        collisions.push(entity);
      }
    }

    return collisions;
  }

  /**
   * Update collision configuration
   * @param config - Partial configuration to update
   */
  updateConfig(config: Partial<CollisionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current collision configuration
   * @returns Current configuration
   */
  getConfig(): CollisionConfig {
    return { ...this.config };
  }

  /**
   * Performance test: Measure collision detection time
   * @param player - Player entity
   * @param entities - Array of entities
   * @returns Time taken in milliseconds
   */
  measurePerformance(player: Player, entities: CollisionEntity[]): number {
    const startTime = performance.now();
    this.detectAllCollisions(player, entities);
    const endTime = performance.now();
    return endTime - startTime;
  }
}

// Singleton instance for global use
export const collisionSystem = new CollisionSystem();
